import { X } from "lucide-react";

import { ZButton } from "@/shared/components/button";
import { ZModal, ZModalBody, ZModalContent } from "@/shared/components/modal";

interface RejectReasonModalProps {
  isOpen: boolean;
  isLoading: boolean;
  rejectionReason: string;
  onReasonChange: (reason: string) => void;
  onClose: () => void;
  onReject: () => void;
}

export function RejectReasonModal({
  isOpen,
  isLoading,
  rejectionReason,
  onReasonChange,
  onClose,
  onReject,
}: RejectReasonModalProps) {
  return (
    <ZModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          onClose();
        }
      }}
      isDismissable={!isLoading}
      aria-label="Reject Assessment Request"
      hideCloseButton
    >
      <ZModalContent
        className="!my-0 relative !w-[512px] !min-w-[512px] !max-w-[512px] overflow-hidden"
        style={{
          borderRadius: "16px",
          boxShadow: "0px 0px 24px rgba(0, 0, 0, 0.05)",
        }}
      >
        {/* X Close button */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute hover:opacity-70 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed z-10"
          style={{
            right: "16px",
            top: "16px",
            width: "16px",
            height: "16px",
          }}
          aria-label="Close"
        >
          <X
            className="text-[#62748E]"
            style={{ width: "16px", height: "16px" }}
            strokeWidth={2}
          />
        </button>

        <ZModalBody
          className="flex flex-col items-center"
          style={{ padding: "24px", gap: "24px" }}
        >
          {/* Header + Subtitle */}
          <div
            className="flex flex-col items-center w-full"
            style={{ gap: "16px" }}
          >
            <h2
              className="font-semibold text-center w-full"
              style={{
                fontSize: "18px",
                lineHeight: "28px",
                color: "#0F172B",
              }}
            >
              Reject Assessment Request
            </h2>

            <p
              className="text-center w-full"
              style={{
                fontSize: "14px",
                lineHeight: "20px",
                color: "#314158",
              }}
            >
              Please provide a reason for rejecting this request. The business
              owner will be notified.
            </p>
          </div>

          {/* Textarea + Button */}
          <div
            className="flex flex-col items-start w-full"
            style={{ gap: "16px" }}
          >
            <textarea
              value={rejectionReason}
              onChange={(e) => onReasonChange(e.target.value)}
              disabled={isLoading}
              placeholder="Enter rejection reason..."
              className="w-full resize-none rounded-lg border border-[#E2E8F0] bg-white text-sm text-[#0F172B] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#006FEE] disabled:opacity-50"
              style={{
                padding: "12px",
                minHeight: "120px",
                fontSize: "14px",
                lineHeight: "20px",
              }}
            />

            <ZButton
              color="danger"
              className="w-full"
              style={{
                height: "40px",
                padding: "8px 16px",
                backgroundColor: "#F31260",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: 500,
                lineHeight: "24px",
              }}
              isDisabled={!rejectionReason.trim() || isLoading}
              isLoading={isLoading}
              onPress={onReject}
            >
              Reject
            </ZButton>
          </div>
        </ZModalBody>
      </ZModalContent>
    </ZModal>
  );
}
